import React from 'react';
import { getCategoryIcon } from '../../../utils/categoryIconMapping';

export interface CategoryIconProps {
  /** Icon name from database (e.g., "music", "utensils") */
  iconName: string | null | undefined;
  /** Icon size in pixels, default 24 */
  size?: number;
  /** Icon color, default uses current text color */
  color?: string;
  /** CSS class name for additional styling */
  className?: string;
  /** Accessibility label for screen readers */
  ariaLabel?: string;
}

/**
 * CategoryIcon - Renders Lucide icon based on category iconName from database
 *
 * Features:
 * - Automatic fallback to Calendar icon if iconName is null/undefined
 * - Supports custom size, color, and styling
 * - Accessible with aria-label support
 *
 * @example
 * // Basic usage
 * <CategoryIcon iconName="music" />
 *
 * @example
 * // With custom size and color
 * <CategoryIcon iconName="sports" size={32} color="#4ECDC4" />
 *
 * @example
 * // With accessibility label
 * <CategoryIcon
 *   iconName="food"
 *   ariaLabel="Food category icon"
 *   className="mr-2"
 * />
 */
export function CategoryIcon({
  iconName,
  size = 24,
  color,
  className = '',
  ariaLabel,
}: CategoryIconProps) {
  const Icon = getCategoryIcon(iconName);

  return (
    <Icon
      size={size}
      color={color}
      className={className}
      aria-label={ariaLabel || (iconName ? `${iconName} icon` : 'category icon')}
      role="img"
    />
  );
}
